import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useRef,
} from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSocket } from '@/contexts/SocketContext';
import {
    getPendingPopups,
    markBroadcastSeen,
    acknowledgeBroadcast as apiAcknowledge,
} from '@/api/platformBroadcasts';

const PlatformBroadcastContext = createContext(null);

/**
 * PlatformBroadcastProvider
 *
 * Mantém uma fila FIFO de comunicados (popups) pendentes para o usuário atual.
 * - No mount (autenticado): busca GET /pending e enfileira.
 * - Em tempo real: escuta socket 'platformBroadcast:new' e enfileira (dedupe por id).
 * - Expõe o popup "da vez" + ações seen/acknowledge.
 */
export function PlatformBroadcastProvider({ children }) {
    const { isAuthenticated } = useAuth();
    const { on } = useSocket();
    const [queue, setQueue] = useState([]);
    const seenIdsRef = useRef(new Set());

    const enqueue = useCallback((broadcast) => {
        if (!broadcast?.id) return;
        setQueue((prev) => {
            const idx = prev.findIndex((b) => b.id === broadcast.id);
            if (idx === -1) return [...prev, broadcast];
            // Atualiza item existente (ex: socket chega com mediaUrl após /pending sem mídia)
            const next = [...prev];
            next[idx] = { ...prev[idx], ...broadcast };
            return next;
        });
    }, []);

    // Carrega pendentes ao autenticar
    useEffect(() => {
        if (!isAuthenticated) {
            setQueue([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { items } = await getPendingPopups();
                if (cancelled || !Array.isArray(items)) return;
                for (const item of items) enqueue(item);
            } catch {
                // silencioso — não bloqueia a aplicação
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, enqueue]);

    // Tempo real via socket
    useEffect(() => {
        if (!isAuthenticated) return undefined;
        const unsub = on('platformBroadcast:new', (payload) => {
            if (payload?.id) enqueue(payload);
        });
        return unsub;
    }, [isAuthenticated, on, enqueue]);

    const current = queue[0] || null;

    // Registra "visto" quando um popup entra em exibição (idempotente no backend)
    useEffect(() => {
        if (!current?.id) return;
        if (seenIdsRef.current.has(current.id)) return;
        seenIdsRef.current.add(current.id);
        markBroadcastSeen(current.id).catch(() => {});
    }, [current]);

    const acknowledge = useCallback(
        async (id, meta = {}) => {
            try {
                await apiAcknowledge(id, meta);
            } catch {
                // mesmo em erro removemos da fila local para não travar o usuário
            }
            setQueue((prev) => prev.filter((b) => b.id !== id));
        },
        [],
    );

    const dismiss = useCallback((id) => {
        setQueue((prev) => prev.filter((b) => b.id !== id));
    }, []);

    const value = { current, queueLength: queue.length, enqueue, acknowledge, dismiss };

    return (
        <PlatformBroadcastContext.Provider value={value}>
            {children}
        </PlatformBroadcastContext.Provider>
    );
}

export function usePlatformBroadcast() {
    const ctx = useContext(PlatformBroadcastContext);
    if (!ctx) {
        throw new Error('usePlatformBroadcast must be used within a PlatformBroadcastProvider');
    }
    return ctx;
}

export default PlatformBroadcastContext;
